import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { scooterApi } from "@/lib/api";
import { toast } from "sonner";
import { MechanicalChecklistForm } from "@/components/MechanicalChecklist";
import { 
  Save, 
  ArrowLeft,
  Info,
  Settings,
  Wrench,
  History
} from "lucide-react";

export default function EditScooterPage() {
  const { scooterId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState(null);
  const [scooterInfo, setScooterInfo] = useState(null);

  useEffect(() => {
    fetchScooter();
  }, [scooterId]);

  const fetchScooter = async () => {
    try {
      const data = await scooterApi.getById(scooterId);
      setScooterInfo({
        scooter_id: data.scooter_id,
        brand: data.brand,
        model: data.model,
        year: data.year,
        vin: data.vin,
        mileage: data.mileage,
      });
      setFormData({
        brand: data.brand,
        model: data.model,
        year: data.year,
        engine_cc: data.engine_cc,
        color: data.color,
        vin: data.vin,
        mileage: data.mileage,
        purchase_price: data.purchase_price,
        selling_price: data.selling_price,
        status: data.status,
        technical_file: data.technical_file || {
          engine_type: "",
          transmission: "",
          cooling_system: "",
          fuel_type: "",
          max_speed: "",
          consumption: "",
        },
        mechanical_check: data.mechanical_check || {
          service_info: { dernier_vidange_effectue: false, vidange_effectue_par: "inconnu", date_derniere_vidange: "", km_derniere_vidange: 0, type_interventions: "periodiques" },
          motor_stare_generala: 3, pornire_motor: 3, compresie_motor: 3, zgomote_anormale: 3, pierderi_ulei: 3,
          ambreiaj: 3, curea_transmisie: 3, variator: 3, role_variator: 3, cutie_transmisie: 3,
          nivel_ulei_motor: 3, calitate_ulei: 3, filtru_ulei: 3, filtru_aer: 3, bujie: 3,
          frana_fata: 3, frana_spate: 3, placute_frana: 3, discuri_frana: 3, conducte_frana: 3, lichid_frana: 3,
          anvelopa_fata: 3, anvelopa_spate: 3, presiune_anvelope: 3, jante: 3, rulmenti_roti: 3, furca_fata: 3, amortizor_spate: 3,
          baterie: 3, incarcare_baterie: 3, alternator: 3, demaror: 3, instalatie_electrica: 3, faruri: 3, stop: 3, semnalizari: 3, claxon: 3, bord: 3,
          cadru: 3, suduri: 3, suport_motor: 3, cric: 3,
          toba_evacuare: 3, garnituri_evacuare: 3, zgomot_evacuare: 3,
          ghidon: 3, acceleratie: 3, manete_frana: 3, oglinzi: 3, blocare_directie: 3,
          carene: 3, sa: 3, portbagaj: 3, stare_generala: 3,
          scor_general: 3, recomandari_mecanic: "", aprobat_utilizare: true,
          acte_disponibile: true, tip_acte: "carte_rar", tara_documente: "", observatii_acte: "",
          observations: "",
        },
        repairs_done: data.repairs_done || "",
        costs: data.costs || 0,
        warranty_info: data.warranty_info || "",
        internal_notes: data.internal_notes || "",
      });
    } catch (error) {
      toast.error("Scooter not found");
      navigate("/stock");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTechnicalChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      technical_file: { ...prev.technical_file, [field]: value }
    }));
  };

  const handleMechanicalCheckChange = (newValues) => {
    setFormData(prev => ({
      ...prev,
      mechanical_check: newValues
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand || !formData.model || !formData.vin) {
      toast.error("Please fill in required fields: Brand, Model, VIN");
      return;
    }

    setSaving(true);
    try {
      await scooterApi.update(scooterId, formData);
      toast.success("Scooter updated successfully!");
      navigate(`/stock/${scooterId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update scooter");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !formData) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-primary font-mono">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="edit-scooter-form">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-heading font-black text-2xl md:text-3xl">
                Edit Scooter
              </h1>
              <p className="text-muted-foreground text-sm font-mono">
                {scooterId}
              </p>
            </div>
          </div>
          <Button 
            type="submit" 
            className="bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
            disabled={saving}
            data-testid="btn-save-changes"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* General Information */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="w-5 h-5 text-primary" />
                General Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand *</Label>
                  <Input
                    id="brand"
                    value={formData.brand}
                    onChange={(e) => handleChange("brand", e.target.value)}
                    className="font-mono"
                    data-testid="input-brand"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Model *</Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => handleChange("model", e.target.value)}
                    className="font-mono"
                    data-testid="input-model"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="year">Year</Label>
                  <Input
                    id="year"
                    type="number"
                    value={formData.year}
                    onChange={(e) => handleChange("year", parseInt(e.target.value))}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="engine_cc">Engine (cc)</Label>
                  <Input
                    id="engine_cc"
                    type="number"
                    value={formData.engine_cc}
                    onChange={(e) => handleChange("engine_cc", parseInt(e.target.value))}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="color">Color</Label>
                  <Input
                    id="color"
                    value={formData.color}
                    onChange={(e) => handleChange("color", e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mileage">Mileage (km)</Label>
                  <Input
                    id="mileage"
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => handleChange("mileage", parseInt(e.target.value))}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vin">VIN *</Label>
                <Input
                  id="vin"
                  value={formData.vin}
                  onChange={(e) => handleChange("vin", e.target.value.toUpperCase())}
                  className="font-mono uppercase"
                  data-testid="input-vin"
                />
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="purchase_price">Purchase Price (EUR)</Label>
                  <Input
                    id="purchase_price"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.purchase_price || ""}
                    onChange={(e) => handleChange("purchase_price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="selling_price">Selling Price (EUR)</Label>
                  <Input
                    id="selling_price"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.selling_price || ""}
                    onChange={(e) => handleChange("selling_price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleChange("status", value)}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_stock">In Stock</SelectItem>
                    <SelectItem value="reserved">Reserved</SelectItem>
                    <SelectItem value="in_service">In Service</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="casse">Casse / Dezmembrare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Technical File */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="w-5 h-5 text-primary" />
                Technical File
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="engine_type">Engine Type</Label>
                  <Input
                    id="engine_type"
                    value={formData.technical_file.engine_type}
                    onChange={(e) => handleTechnicalChange("engine_type", e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transmission">Transmission</Label>
                  <Input
                    id="transmission"
                    value={formData.technical_file.transmission}
                    onChange={(e) => handleTechnicalChange("transmission", e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cooling_system">Cooling System</Label>
                  <Input
                    id="cooling_system"
                    value={formData.technical_file.cooling_system}
                    onChange={(e) => handleTechnicalChange("cooling_system", e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fuel_type">Fuel Type</Label>
                  <Input
                    id="fuel_type"
                    value={formData.technical_file.fuel_type}
                    onChange={(e) => handleTechnicalChange("fuel_type", e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max_speed">Max Speed</Label>
                  <Input
                    id="max_speed"
                    value={formData.technical_file.max_speed}
                    onChange={(e) => handleTechnicalChange("max_speed", e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="consumption">Consumption</Label>
                  <Input
                    id="consumption"
                    value={formData.technical_file.consumption}
                    onChange={(e) => handleTechnicalChange("consumption", e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mechanical Checklist */}
          <Card className="border-border lg:col-span-2">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Wrench className="w-5 h-5 text-primary" />
                Fiche de Contrôle Mécanique / Mechanical Checklist
              </CardTitle>
            </CardHeader>
            <CardContent>
              <MechanicalChecklistForm
                values={formData.mechanical_check}
                onChange={handleMechanicalCheckChange}
                scooterInfo={scooterInfo}
              />
            </CardContent>
          </Card>

          {/* History & Notes */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <History className="w-5 h-5 text-primary" />
                History & Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="repairs_done">Repairs Done</Label>
                <Textarea
                  id="repairs_done"
                  value={formData.repairs_done}
                  onChange={(e) => handleChange("repairs_done", e.target.value)}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="costs">Total Costs (€)</Label>
                  <Input
                    id="costs"
                    type="number"
                    value={formData.costs}
                    onChange={(e) => handleChange("costs", parseFloat(e.target.value))}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="warranty_info">Warranty</Label>
                  <Input
                    id="warranty_info"
                    value={formData.warranty_info}
                    onChange={(e) => handleChange("warranty_info", e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="internal_notes">Internal Notes</Label>
                <Textarea
                  id="internal_notes"
                  value={formData.internal_notes}
                  onChange={(e) => handleChange("internal_notes", e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submit Button (Mobile) */}
        <div className="lg:hidden">
          <Button 
            type="submit" 
            className="w-full bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Layout>
  );
}
